import axios from 'axios';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';

/**
 * Axios instance configured for the Atlas Restoration API
 * - Base URL from environment variables with /api prefix
 * - JWT token injection via request interceptor
 * - 401 error handling via response interceptor
 *
 * Note: All API requests automatically get /api prepended to their routes.
 * Individual API calls should NOT include /api in their paths.
 * Example: use '/authentication/login' not '/api/authentication/login'
 */
const apiClient = axios.create({
  baseURL: `${import.meta.env.VITE_API_BASE_URL || 'https://localhost:7001'}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

/**
 * Request interceptor to attach JWT token to all requests
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('auth_token');

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor to handle authentication errors
 * - Redirects to login on 401 Unauthorized
 * - Clears stored token
 */
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Clear token
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');

      // Redirect to login (avoiding circular dependency)
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
